import { useState } from 'react';

import { useCreditCardContext } from 'contexts/CreditCardDetailsContextProvider';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useCreateSetupIntentAndStripeSetupIntent from 'hooks/useCreateSetupIntentAndStripeSetupIntent';

import useTrialCreationVariables from './useTrialCreationVariables';

const useTrialPayment = () => {
  const { handleStripeAndSetupIntent, stripeNotLoaded } =
    useCreateSetupIntentAndStripeSetupIntent(true);
  const { hasValidCreditCard, zip } = useCreditCardContext();
  const {
    baseVariables,
    setTrialCreationError,
    shouldDisplayError,
    trackEvent,
  } = useTrialCreationVariables();

  const { hasValidName, setSetupIntentData } = useTrialsContext() || {};

  const [isSubmitting, setIsSubmitting] = useState(null);

  const isReadyToStartSubmit = (event) => {
    trackEvent(`trials.startTrial.startTrialClicked`);
    shouldDisplayError(false);
    setIsSubmitting(true);
    // Block native form submission.
    event.preventDefault();

    if (stripeNotLoaded) {
      trackEvent(`trials.startTrial.error`, {
        error: 'Stripe not loaded',
      });
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setIsSubmitting(false);
      return false;
    }
    return true;
  };

  const hasValidCreditCardAndName = () => {
    const validName = hasValidName();
    const validCreditCard = hasValidCreditCard();

    return validName && validCreditCard;
  };

  const handlePaymentButtonSubmit = async (event) => {
    if (!isReadyToStartSubmit(event)) {
      return;
    }

    if (!hasValidCreditCardAndName()) {
      return setIsSubmitting(false);
    }

    try {
      // CREATE SETUP INTENT AND GET SECRET
      const {
        createSetupIntentError,
        setUpIntentData,
        stripeSetUpIntent,
        stripeError,
      } =
        (await handleStripeAndSetupIntent({
          ...baseVariables,
          postalCode: zip || baseVariables?.postalCode,
        })) || {};

      if (createSetupIntentError) {
        return setTrialCreationError(createSetupIntentError.message);
      }

      if (stripeError) {
        return setTrialCreationError(stripeError, true);
      }

      const data = {
        stripeCustomerId: setUpIntentData?.createSetupIntent?.id,
        stripePaymentMethodId: stripeSetUpIntent?.payment_method,
        stripeSetupIntentId: stripeSetUpIntent?.id,
      };

      setSetupIntentData(data);

      return data;
    } catch (error) {
      setTrialCreationError(error);
    }
  };

  return { handlePaymentButtonSubmit, isSubmitting, setIsSubmitting };
};

export default useTrialPayment;
